/**
 * Step1Source — Choix de la source de refacturation (async + batch).
 *
 * 2 cards :
 *   - Upload PDF fournisseur (extraction Gemini async avec polling)
 *   - Choisir une facture fournisseur déjà en base (search + list)
 *
 * Nouveauté V2.2 : upload multi-fichiers → grid de mini-cards status.
 */
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Upload, Layers, Loader2, Sparkles, CheckCircle2, XCircle, Clock,
  FileText, ArrowRight,
} from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { eur } from "./utils";

function JobRowStatus({ job, onContinue }) {
  const isDone = job.status === "done";
  const isError = job.status === "error";
  const isProcessing = job.status === "processing";
  const Icon = isDone ? CheckCircle2 : isError ? XCircle : isProcessing ? Loader2 : Clock;
  const color = isDone ? "text-emerald-600"
    : isError ? "text-rose-600"
    : isProcessing ? "text-violet-600"
    : "text-slate-400";
  return (
    <div
      className={cn(
        "flex items-center gap-3 border rounded-lg p-2.5 text-sm bg-white transition",
        isDone ? "border-emerald-200 bg-emerald-50/40"
          : isError ? "border-rose-200 bg-rose-50/30"
          : "border-slate-200",
      )}
      data-testid={`rebilling-job-${job.id}`}>
      <Icon className={cn("h-4 w-4 flex-shrink-0",
        isProcessing && "animate-spin", color)} />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-slate-900 truncate">
          {job.filename || "?"}
        </div>
        {isDone && (
          <div className="text-[11px] text-slate-500 truncate">
            {job.extracted_supplier?.name || "Fournisseur"} · {job.extraction_line_count} ligne(s)
          </div>
        )}
        {isError && (
          <div className="text-[11px] text-rose-600 truncate">
            {job.error || "Erreur inconnue"}
          </div>
        )}
        {(job.status === "pending" || isProcessing) && (
          <div className="text-[11px] text-slate-500">
            {isProcessing ? "Analyse en cours…" : "En attente…"}
          </div>
        )}
      </div>
      {isDone && (
        <Button
          size="sm"
          onClick={() => onContinue?.(job)}
          className="h-7 px-2.5 text-xs bg-violet-600 hover:bg-violet-700 text-white gap-1 flex-shrink-0"
          data-testid={`rebilling-job-continue-${job.id}`}>
          Continuer <ArrowRight className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

function UploadProgress() {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setElapsed((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <>
      <div className="relative inline-block mb-2">
        <Loader2 className="h-8 w-8 text-violet-600 animate-spin" />
        <Sparkles className="h-3 w-3 text-fuchsia-500 absolute -top-1 -right-1 animate-pulse" />
      </div>
      <p className="text-sm text-slate-700 font-medium">Envoi et création du job…</p>
      <p className="text-[11px] text-slate-400 mt-1 tabular-nums">{elapsed}s</p>
    </>
  );
}

export default function Step1Source({
  sourceMode, setSourceMode,
  uploading, onUpload, uploadError, onRetryUpload,
  batchJobs, onContinueJob,
  supplierList, supplierSearch, onSearchChange,
  loadingSupplierList, onSupplierPick,
}) {
  const hasJobs = (batchJobs || []).length > 0;
  const doneCount = (batchJobs || []).filter((j) => j.status === "done").length;
  const totalJobs = (batchJobs || []).length;

  const handleFilesChange = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const arr = Array.from(fileList);
    onUpload(arr.length === 1 ? arr[0] : arr);
  };

  return (
    <div className="grid md:grid-cols-2 gap-4">
      {/* Option A : Upload PDF (async + batch) */}
      <Card
        className={cn(
          "border rounded-xl p-6 cursor-pointer transition-all",
          sourceMode === "upload"
            ? "border-violet-400 bg-violet-50/40 ring-1 ring-violet-200"
            : "border-slate-200 bg-white hover:border-violet-300"
        )}
        onClick={() => setSourceMode("upload")}
        data-testid="source-mode-upload">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="h-11 w-11 rounded-lg bg-violet-100 text-violet-700 flex items-center justify-center">
            <Upload className="h-5 w-5" />
          </div>
        </div>
        <div className="font-semibold text-slate-900">Uploader un ou plusieurs PDF</div>
        <p className="text-sm text-slate-500 mt-1">
          Extraction IA <b>asynchrone</b> — glisse un ou plusieurs PDF, chacun est traité en parallèle et tu peux enchaîner.
        </p>

        {sourceMode === "upload" && (
          <>
            <label
              className={cn(
                "block mt-4 p-6 border-2 border-dashed rounded-lg cursor-pointer text-center transition",
                uploading ? "border-violet-300 bg-white cursor-wait"
                  : uploadError ? "border-rose-300 bg-rose-50/50"
                  : "border-violet-300 hover:bg-white",
              )}
              data-testid="rebilling-upload-zone">
              <input
                id="rebilling-file-input"
                name="rebilling-file-input"
                type="file"
                accept="application/pdf,image/*"
                className="hidden"
                multiple
                disabled={uploading}
                onChange={(e) => handleFilesChange(e.target.files)}
                data-testid="rebilling-file-input"
              />
              {uploading ? (
                <UploadProgress />
              ) : uploadError ? (
                <>
                  <div className="mx-auto w-8 h-8 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mb-2">!</div>
                  <p className="text-sm text-slate-700 font-medium">{uploadError}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e.preventDefault(); onRetryUpload?.(); }}
                    className="mt-3 border-slate-200"
                    data-testid="rebilling-upload-retry">
                    Réessayer
                  </Button>
                </>
              ) : (
                <>
                  <Upload className="h-7 w-7 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm font-medium text-slate-700">
                    Glisse un ou plusieurs PDF
                  </p>
                  <p className="text-xs text-slate-400 mt-1">10 Mo max par fichier · sélection multiple ok</p>
                </>
              )}
            </label>

            {/* Progression des jobs en cours */}
            {hasJobs && (
              <div className="mt-4 space-y-2" data-testid="rebilling-batch-progress">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-medium text-slate-700 flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5" />
                    {doneCount}/{totalJobs} extraction(s) terminée(s)
                  </div>
                </div>
                <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                  {batchJobs.map((j) => (
                    <JobRowStatus key={j.id} job={j} onContinue={onContinueJob} />
                  ))}
                </div>
                {totalJobs > 1 && doneCount > 0 && (
                  <p className="text-[11px] text-slate-500 italic">
                    Clique sur "Continuer" pour refacturer la facture de ton choix — les autres restent disponibles ici.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </Card>

      {/* Option B : Choisir depuis DB */}
      <Card
        className={cn(
          "border rounded-xl p-6 cursor-pointer transition-all",
          sourceMode === "existing"
            ? "border-violet-400 bg-violet-50/40 ring-1 ring-violet-200"
            : "border-slate-200 bg-white hover:border-violet-300"
        )}
        onClick={() => setSourceMode("existing")}
        data-testid="source-mode-existing">
        <div className="h-11 w-11 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center mb-3">
          <Layers className="h-5 w-5" />
        </div>
        <div className="font-semibold text-slate-900">Choisir une facture déjà en base</div>
        <p className="text-sm text-slate-500 mt-1">
          Sélectionne une facture fournisseur (Axonaut, Pennylane, OCR IA) déjà présente
          — les lignes sont déjà propres.
        </p>
        {sourceMode === "existing" && (
          <div className="mt-4 space-y-2">
            <Input
              placeholder="Rechercher un fournisseur ou n° facture…"
              value={supplierSearch}
              onChange={(e) => onSearchChange(e.target.value)}
              className="bg-white"
              data-testid="rebilling-supplier-search"
            />
            <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100 bg-white">
              {loadingSupplierList ? (
                <div className="p-4 text-center text-sm text-slate-400">
                  <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Chargement…
                </div>
              ) : supplierList.length === 0 ? (
                <div className="p-4 text-center text-sm text-slate-400">
                  Aucune facture trouvée
                </div>
              ) : supplierList.map((inv) => (
                <button
                  key={inv.id}
                  className="w-full text-left px-3 py-2 hover:bg-violet-50 text-sm"
                  onClick={() => onSupplierPick(inv.id)}
                  data-testid={`rebilling-supplier-item-${inv.id}`}>
                  <div className="flex justify-between items-center gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">
                        {inv.supplier_name || "Fournisseur inconnu"}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {inv.invoice_number || "—"} · {(inv.invoice_date || "").slice(0, 10)}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono text-sm text-slate-900">
                        {eur(inv.total_ttc || 0)}
                      </div>
                      <div className="text-[10px] uppercase text-slate-400">
                        {inv.source_kind || inv.source || "manuel"}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
